import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatNavList } from '@angular/material/list';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslocoDirective, TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { environment } from '@htx/environment';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Subscription } from 'rxjs';
import { IdleService, SlideOverPanelComponent } from '@htx/core';
import { AccessDeniedReason, evaluateAccess } from './auth/access-check';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    MatNavList,
    MatToolbar,
    RouterLink,
    RouterLinkActive,
    TranslocoDirective,
    TranslocoPipe,
    MatMenu,
    MatMenuTrigger,
    MatSlideToggle,
    MatButton,
    SlideOverPanelComponent
  ],
  styleUrl: './app.component.scss',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  readonly translocoService = inject(TranslocoService);
  readonly router = inject(Router);
  readonly oauthService = inject(OAuthService);
  readonly idleService = inject(IdleService);

  title = 'ht-x-connector-management-ui';
  isEnglish = false;

  isLoggedIn$ = new BehaviorSubject<boolean>(false);
  userRoles$ = new BehaviorSubject<string[]>([]);
  accessDenied$ = new BehaviorSubject<AccessDeniedReason | null>(null);
  username = '';
  private eventsSub?: Subscription;

  ngOnInit() {
    const active = this.translocoService.getActiveLang();
    this.isEnglish = active === 'en';

    if (!environment.oauth) {
      this.isLoggedIn$.next(true);
      return;
    }

    this.eventsSub = this.oauthService.events.subscribe((e) => {
      if (e.type === 'session_terminated' || e.type === 'session_error') {
        this.logout();
      }
    });

    if (this.oauthService.hasValidAccessToken()) {
      this.applyTokenAndSession();
    } else {
      this.oauthService.initLoginFlow();
    }
  }

  ngOnDestroy(): void {
    this.eventsSub?.unsubscribe();
  }

  private applyTokenAndSession() {
    if (!environment.oauth) {
      return;
    }
    const result = evaluateAccess(this.oauthService.getAccessToken(), environment.oauth);
    if (!result.allowed) {
      this.accessDenied$.next(result.reason);
      this.isLoggedIn$.next(false);
      this.userRoles$.next([]);
      this.username = '';
      this.idleService.stopWatching();
      return;
    }
    this.accessDenied$.next(null);
    this.userRoles$.next(result.roles);
    this.username = result.username;
    this.isLoggedIn$.next(true);
    this.idleService.startWatching();
  }

  logout() {
    this.idleService.stopWatching();
    this.oauthService.logOut();
    this.isLoggedIn$.next(false);
    this.accessDenied$.next(null);
    this.router.navigate(['/']);
  }

  onToggleLanguage(isEn: boolean) {
    this.isEnglish = isEn;
    this.translocoService.setActiveLang(isEn ? 'en' : 'de');
  }

  get tenantAlias(): string {
    return environment.oauth?.tenantOrganizationAlias ?? '';
  }

  routeList: MenuItem[] = [
    /**
     * t(menu.dashboard)
     */
    {
      icon: 'assets/images/dashboard-icon.svg',
      activeIcon: 'assets/images/dashboard-filled-icon.svg',
      nameKey: 'menu.dashboard',
      link: '/dashboard'
    },
    /**
     * t(menu.catalog)
     */
    {
      icon: 'assets/images/catalog-icon.svg',
      activeIcon: 'assets/images/catalog-filled-icon.svg',
      nameKey: 'menu.catalog',
      link: '/catalog'
    },
    /**
     * t(menu.services)
     */
    {
      icon: 'assets/images/assets-icon.svg',
      activeIcon: 'assets/images/assets-filled-icon.svg',
      nameKey: 'menu.services',
      link: '/assets'
    },
    /**
     * t(menu.policies)
     */
    {
      icon: 'assets/images/policy-icon.svg',
      activeIcon: 'assets/images/policy-filled-icon.svg',
      nameKey: 'menu.policies',
      link: '/policies'
    },
    /**
     * t(menu.contracts)
     */
    {
      icon: 'assets/images/contract-icon.svg',
      activeIcon: 'assets/images/contract-filled-icon.svg',
      nameKey: 'menu.contracts',
      link: '/contracts'
    },
    /**
     * t(menu.connectorProperties)
     */
    {
      icon: 'assets/images/connector-properties-icon.svg',
      activeIcon: 'assets/images/connector-properties-filled-icon.svg',
      nameKey: 'menu.connectorProperties',
      link: '/connector'
    }
  ];

  protected readonly environment = environment;
}

interface MenuItem {
  icon: string;
  activeIcon: string;
  nameKey: string;
  link: string;
}
